import { Button, Callout, Collapse, ControlGroup, FileInput, FormGroup, InputGroup, ProgressBar } from "@blueprintjs/core";
import { IconNames } from "@blueprintjs/icons";
import TextWidget from "common/form/widgets/TextWidget";
import VerticalCenter from "common/fragments/VerticalCenter";
import EditorToaster from "core/components/editor/EditorToaster";
import LabelUtil from "core/components/label/LabelUtil";
import { IUploadError, IUploadOptions, IUploadResponse, IUploadService } from "core/service/upload/IUploadService";
import FileUtil from "core/util/FileUtil";
import ServiceRegistry from "core/util/ServiceRegistry";
import { PositionProperty } from "csstype";
import { IFieldProps } from "decorator-form";
import React from "react";

export interface IFileUpload extends IUploadResponse {

}

export interface IUploadFieldOptions extends IUploadOptions {
	urlEditable?: boolean;
	showPreview?: boolean;
}

interface IUploadFieldProps extends IFieldProps<IFileUpload> {
	options?: IUploadFieldOptions;
}

interface IUploadFieldState {
	errors: null | IUploadError[];
	file: {
		name: string;
		size: number;
	};
	expanded: boolean;
	progress: number;
	uploading: boolean;
	url: string;
}

const styles = {
	error: {
		padding: '0.7em 0',
		marginBottom: '1em'
	},
	wrapper: {
		backgroundColor: 'rgba(0,0,0,0.2)',
		border: '1px solid rgba(0,0,0,0.2)',
		borderRadius: '4px',
		width: '100%',
		minHeight: '50px',
		position: 'relative' as PositionProperty,
		overflow: 'hidden'
	},
	img: {
		maxWidth: '100%'
	},
	label: {
		position: 'absolute' as PositionProperty,
		bottom: 15,
		left: 15,
		backgroundColor: 'rgba(0,0,0,0.5)',
		borderRadius: '5px',
		padding: '5px'
	}
};

export class UploadField extends React.Component<IUploadFieldProps, IUploadFieldState> {

	public static withOptions(options: IUploadFieldOptions) {
		return (props: IFieldProps<IFileUpload>) => <UploadField {...props} options={options} />;
	}

	private inputRef: React.RefObject<HTMLInputElement>;

	public constructor(props: IUploadFieldProps) {
		super(props);
		this.inputRef = React.createRef();
		this.state = {
			errors: null,
			expanded: true,
			file: null,
			progress: 0,
			uploading: false,
			url: ""
		};
	}

	public render() {
		const { disabled, formData, options = { showPreview: true, urlEditable: true }, required, schema = {} } = this.props;

		return [
			<FormGroup label={schema.title + (required ? "*" : '')} helperText={schema.description} key="upload">
				{
					this.state.errors && this.state.errors.map(this.renderError)
				}
				<ControlGroup fill={true}>
					{ <FileInput
						style={{marginBottom: 0}}
						disabled={disabled}
						inputProps={{ref: this.inputRef}}
						text={this.state.file ? `${this.state.file.name}` : 'Choose File'}
						hasSelection={!!this.state.file}
						onInputChange={this.onInputChange}
					/> }
					<Button icon={IconNames.UPLOAD} disabled={!this.state.file} onClick={this.onSubmit} loading={this.state.uploading} />
				</ControlGroup>
			</FormGroup>,
			(options.urlEditable && (
				<FormGroup
					key="url"
					intent={formData && formData.url && !formData.url.startsWith('https') ? "danger" : "none"}
					helperText="HTTPS URLs only"
				>
					<TextWidget value={""} onChange={this.onManualUrlChange} requireConfirm={true} />
				</FormGroup>
			)),
			(options.showPreview && (!!formData && !!formData.url && (
				<FormGroup key="preview">
					{this.renderPreview(formData)}
				</FormGroup>
			)))
		];
	}

	private renderPreview = (upload: IUploadResponse) => {

		if (upload.type && upload.type.startsWith('image')) {

			return (
				<div style={styles.wrapper}>
					<VerticalCenter>
						<img src={upload.url} style={styles.img} />
					</VerticalCenter>
					{
						upload.name && (
							<span style={styles.label}>
								{upload.name}
							</span>
						)
					}
				</div>
			);
		}

		return upload.name;
	}

	private renderError = (error: IUploadError, index: number) => {

		return (
			<Callout intent="danger" style={styles.error} key={index}>
				{LabelUtil.formatTemplate(error.template, error.args)}
			</Callout>
		);
	}

	private onManualUrlChange = (url: string) => {

		this.props.onChange({url, type: FileUtil.guessMimeTypeFromExtension(url), name: url});
	}

	/**
	 * validate the input contents and update ui state
	 */
	private onInputChange = () => {
		const file = this.getFile();
		if (file != null) {
			this.setState({
				file: {
					name: file.name,
					size: file.size
				}
			});
		} else {
			this.setState({file: null});
		}
	}

	/**
	 * commence upload
	 */
	private onSubmit = () => {

		const file = this.getFile();

		if (file) {
			this.setState({errors: null, uploading: true}, () => {
				ServiceRegistry
					.get(IUploadService)
					.upload(file, this.props.options)
					.then(this.onSuccess)
					.catch(this.onFailure);
			});
		}
	}

	private onSuccess = (res: IUploadResponse) => {
		EditorToaster.show({
			message: 'Upload Successful',
			intent: 'success'
		});
		this.setState({
			uploading: false,
			file: null,
		}, () => {
			this.props.onChange(res);
		});
	}

	private onFailure = (errors: IUploadError[]) => {
		EditorToaster.show({
			message: 'Upload Failed.',
			intent: 'danger'
		});
		this.setState({
			errors,
			uploading: false
		});
	}

	/**
	 * get the file from the current input ref
	 */
	private getFile = (): File | null => {
		const { current } = this.inputRef;
		if (!current || !current.files || current.files.length < 1) {
			return null;
		}
		return current.files[0];
	}
}



// WEBPACK FOOTER //
// ./src/common/form/fields/upload/UploadField.tsx